import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const StatusUpdateModal = ({
  isOpen,
  onClose,
  currentStatus,
  onUpdate,
  isUpdating = false
}) => {
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');

  // Auto-set to cancelled when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewStatus('cancelled');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Prevent updates if status is already completed
  const isCompleted = currentStatus === 'completed';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (newStatus && newStatus !== currentStatus) {
      onUpdate(newStatus, statusReason);
    }
  };

  const handleClose = () => {
    setNewStatus('');
    setStatusReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg max-w-md w-full relative">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="p-6 border-b-2 border-black">
          <h2 className="text-xl font-bold text-black mb-2">Cancel Booking</h2>
          {isCompleted ? (
            <p className="text-sm text-red-600 font-medium">
              This booking is already completed and cannot be cancelled.
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              Cancel this booking
            </p>
          )}
        </div>

        {/* Form */}
        {isCompleted ? (
          <div className="p-6">
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-700">
                Completed bookings cannot be modified.
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Status
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-700">
                {currentStatus?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Cancellation <span className="text-red-500">*</span>
              </label>
              <div className="px-3 py-2 bg-red-50 border border-red-300 rounded-lg text-sm text-red-700 font-medium">
                Cancelled
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="Reason for status change..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-black resize-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t-2 border-gray-300 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border-2 border-black text-black rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!newStatus || newStatus === currentStatus || isUpdating}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? 'Cancelling...' : 'Cancel Booking'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default StatusUpdateModal;

